import React, { useEffect, useRef, useState } from "react";
import { JobApplication } from "../../../../Entities/JobApplication";
import { PaginationType, JobApplicationFilters } from "../../../../Interface";
import {
  Button,
  Dropdown,
  Flex,
  Popover,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import type { MenuProps } from "antd";
import useOrganization from "../../../../Hooks/useOrganization";
import {
  EyeOutlined,
  MailOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { capitalizeFirstLetter } from "../../../../Utils/generic-functions";
import { isEligibleForResumeReminder } from "../../../../Utils/applicationEligibility";
import { openNotification } from "../../../../Components/Notification";
import emailService from "../../../../Service/email.service";
import { useLoader } from "../../../../Context/LoaderContext";
import interviewService from "../../../../Service/interview.service";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import { useNavigate } from "react-router-dom";
import "./JobApplicationTable.scss";

interface JobApplicationTableProps {
  jobApplications: JobApplication[];
  pagination: PaginationType;
  tab: string;
  idInQuery: string | null;
  onPaginationChange: (page: number, pageSize?: number) => void;
  jobPost: JobDescriptionEntity;
  onSortChange?: (
    sortKey: string,
    sortOrder: "ascend" | "descend" | null,
  ) => void;
  selectedRowKeys?: string[];
  onSelectChange?: (
    selectedRowKeys: string[],
    selectedRows: JobApplication[],
  ) => void;
  activeFilters?: JobApplicationFilters;
}

const JobApplicationTable: React.FC<JobApplicationTableProps> = ({
  jobApplications,
  pagination,
  tab,
  idInQuery,
  onPaginationChange,
  jobPost,
  onSortChange,
  selectedRowKeys = [],
  onSelectChange,
  activeFilters,
}) => {
  const [tableScrollHeight, setTableScrollHeight] = useState(400);
  const tableRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { org } = useOrganization();
  const timeZone = org?.timeZone;
  const themeColor = org?.theme?.primaryColor || "#1890ff";

  const { setLoading, setLoadingMessage } = useLoader();

  const isActionRequiredRow = (record: JobApplication): boolean => {
    const status = record.questionnaireStatus ?? (record as any).roundStatus;
    return status === "action_required" || status === "partially_qualified";
  };

  // Helper function to check if a column is filtered
  const isColumnFiltered = (columnKey: string): boolean => {
    if (!activeFilters) return false;

    // Map column keys to filter keys from JobApplicationFilters interface
    const columnToFilterMap: Record<string, keyof JobApplicationFilters> = {
      applicationStatus: "status",
      profileMatchStatus: "profileMatchSatus",
      questionnaireStatus: "roundStatus",
      interviewStatus: "interviewStatus",
      candidate: "search",
      appliedDate: "sort",
    };

    const filterKey = columnToFilterMap[columnKey];
    if (!filterKey) return false;

    const filterValue = activeFilters[filterKey];

    // For sort, check if it's sorting by the relevant field
    // sort: "latest_first" | "oldest_first" | "profile_score_highest" | "profile_score_lowest" | null
    if (filterKey === "sort") {
      const sortValue = activeFilters.sort;
      if (columnKey === "appliedDate") {
        return sortValue === "latest_first" || sortValue === "oldest_first";
      }
      if (columnKey === "profileMatchStatus") {
        return (
          sortValue === "profile_score_highest" ||
          sortValue === "profile_score_lowest"
        );
      }
      return false;
    }

    // For search, check if search is active
    // search: string | null
    if (filterKey === "search") {
      return (
        filterValue !== null && filterValue !== undefined && filterValue !== ""
      );
    }

    // For other filters, check if value is not null
    return filterValue !== null && filterValue !== undefined;
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  const handleTableRowClick = (e: React.MouseEvent, record: JobApplication) => {
    e.stopPropagation();
    // Encode current URL to preserve filters, pagination, sort on back navigation
    const currentUrl = window.location.pathname + window.location.search;
    const encodedReturnUrl = encodeURIComponent(currentUrl);
    navigate(
      `/application-details?applicationId=${record._id}&jobId=${idInQuery}&returnUrl=${encodedReturnUrl}`,
    );
  };

  const handleSendEmail = async (jobApplication: JobApplication) => {
    try {
      setLoading(true);
      setLoadingMessage("Sending email to candidate...");

      await emailService.sendResumeUploadReminder(
        jobApplication._id,
        jobApplication.orgId,
      );

      openNotification(
        "success",
        "Email Sent",
        "Resume upload reminder email has been sent to the candidate successfully.",
      );
    } catch (error: any) {
      console.error("Error sending email:", error);
      openNotification(
        "error",
        "Email Failed",
        "Failed to send email to candidate. Please try again.",
      );
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const handleStartInterview = async (applicationId: string) => {
    const scheduleData =
      await interviewService.getInterviewScheduleByJobApplicationId(
        applicationId,
      );
    const path: string = `https://meet.google.com/${scheduleData?.conferenceId}`;
    window.open(path, "_blank");
  };

  const handleViewApplication = (applicationId: string) => {
    // Encode current URL to preserve filters, pagination, sort on back navigation
    const currentUrl = window.location.pathname + window.location.search;
    const encodedReturnUrl = encodeURIComponent(currentUrl);
    navigate(
      `/application-details?applicationId=${applicationId}&jobId=${idInQuery}&returnUrl=${encodedReturnUrl}`,
    );
  };

  const columns: TableColumnsType<JobApplication> = [
    {
      title: "Applied Date",
      dataIndex: "createdAt",
      key: "appliedDate",
      minWidth: 150,
      sorter: true,
      sortDirections: ["descend", "ascend"],
      className: isColumnFiltered("appliedDate") ? "filtered-column" : "",
      render: (data) => {
        return (
          <Flex vertical gap={2}>
            <Typography.Text strong>
              {dayjs(data).format("ddd DD MMM YYYY")}
            </Typography.Text>
            <Typography.Text type="secondary" className="text-secondary-small">
              {dayjs(data).format("hh:mm A")}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: "Candidate",
      dataIndex: "user",
      key: "candidate",
      minWidth: 200,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      className: isColumnFiltered("candidate") ? "filtered-column" : "",
      render: (data, record: JobApplication) => {
        return (
          <Flex vertical gap={2}>
            <Typography.Text strong>
              {capitalizeFirstLetter(data?.firstName)}{" "}
              {capitalizeFirstLetter(data?.lastName)}
            </Typography.Text>
            <Typography.Text type="secondary" className="text-secondary-small">
              {data?.email}
            </Typography.Text>
            {record.uploadType === "zoho" && (
              <img
                src="/images/zoho-recruit-logo.png"
                alt="Zoho Recruit"
                className="zoho-recruit-badge"
                title="Imported from Zoho Recruit"
              />
            )}
          </Flex>
        );
      },
    },
    {
      title: "Profile Screening",
      dataIndex: "profileMatchStatus",
      key: "profileMatchStatus",
      minWidth: 200,
      sorter: (a, b) => {
        const scoreA = a.profileMatchStatus?.score || 0;
        const scoreB = b.profileMatchStatus?.score || 0;
        return scoreA - scoreB;
      },
      sortDirections: ["descend", "ascend"],
      className: isColumnFiltered("profileMatchStatus")
        ? "filtered-column"
        : "",
      render: (data) => {
        // If no data, show status tag with empty stars
        if (!data) {
          return (
            <Tag color="orange" className="profile-screening-tag">
              <Flex align="center" gap={2}>
                {Array.from({ length: 5 }, (_, index) => (
                  <StarOutlined
                    key={`empty-${index}`}
                    className="star-icon-empty"
                  />
                ))}
              </Flex>
              <Typography.Text
                className="profile-screening-status-text"
                style={{ color: "#d46b08" }}
              >
                Pending
              </Typography.Text>
            </Tag>
          );
        }

        const score = data?.score || 0;
        const isQualified = data?.status === "qualified";
        const status = data?.status || "pending";

        // Calculate number of filled stars based on score percentage
        const filledStars = Math.round((score / 100) * 5);
        const emptyStars = 5 - filledStars;

        // Determine tag color based on status
        let tagColor: string;
        let statusText: string;
        let textColor: string;

        if (status === "qualified") {
          tagColor = "green";
          statusText = "Qualified";
          textColor = "#389e0d"; // Green text
        } else if (status === "unqualified") {
          tagColor = "red";
          statusText = "Unqualified";
          textColor = "#cf1322"; // Red text
        } else {
          tagColor = "orange";
          statusText = "Pending";
          textColor = "#d46b08"; // Orange text
        }

        return (
          <Tag color={tagColor} className="profile-screening-tag">
            <Flex align="center" gap={2}>
              {Array.from({ length: filledStars }, (_, index) => (
                <StarFilled
                  key={`filled-${index}`}
                  className={`star-icon-filled ${!isQualified ? "unqualified" : ""}`}
                />
              ))}
              {Array.from({ length: emptyStars }, (_, index) => (
                <StarOutlined
                  key={`empty-${index}`}
                  className="star-icon-empty"
                />
              ))}
            </Flex>
            <Typography.Text
              className="profile-screening-status-text"
              style={{ color: textColor }}
            >
              {statusText}
            </Typography.Text>
          </Tag>
        );
      },
    },
  ];

  if (
    jobPost &&
    jobPost.interviewRounds &&
    jobPost.interviewRounds.length > 0
  ) {
    columns.push({
      title: "AI Assessment",
      dataIndex: "questionnaireStatus",
      key: "questionnaireStatus",
      minWidth: 150,
      className: isColumnFiltered("questionnaireStatus")
        ? "filtered-column"
        : "",
      render: (data, record: JobApplication) => {
        if (data === "partially_qualified") {
          return (
            <Button
              type="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const currentUrl =
                  window.location.pathname + window.location.search;
                const encodedReturnUrl = encodeURIComponent(currentUrl);
                navigate(
                  `/application-details?applicationId=${record._id}&jobId=${idInQuery}&tab=assessments&returnUrl=${encodedReturnUrl}`,
                );
              }}
            >
              Action Required
            </Button>
          );
        }

        if (data === "action_required") {
          return (
            <Popover
              placement="top"
              title={"Assessment Generation Blocked"}
              content={
                "Assessment generation is blocked for this candidate because you have no remaining assessments in your current subscription plan"
              }
              overlayInnerStyle={{ maxWidth: 280 }}
            >
              <Tag color="red">Not Generated</Tag>
            </Popover>
          );
        }

        switch (data) {
          case "qualified":
            return <Tag color="green">Qualified</Tag>;
          case "unqualified":
            return <Tag color="red">Unqualified</Tag>;
          case "pending":
            return <Tag color="orange">In Progress</Tag>;
          default:
            // return <Tag color="gray">NA</Tag>;
            return <Typography.Text type="danger">{data}</Typography.Text>;
        }
      },
    });
  }

  if (jobPost && jobPost.onlineInterview) {
    columns.push({
      title: "Interview",
      dataIndex: "interviewSchedule",
      key: "interviewStatus",
      minWidth: 200,
      className: isColumnFiltered("interviewStatus") ? "filtered-column" : "",
      render: (interviewSchedule, record: JobApplication) => {
        // Handle "Slot Not Selected" case
        if (!record.interviewSchedule && record.status === "interview") {
          return (
            <div className="column-info-container">
              <Tag color="blue">Pending Slot</Tag>
              <Typography.Text type="secondary" className="text-secondary-small">
                Awaiting slot selection
              </Typography.Text>
            </div>
          );
        }

        // Handle no interview schedule
        if (!interviewSchedule) {
          return (
            <div className="column-info-container">
              <Typography.Text type="danger">-</Typography.Text>
            </div>
          );
        }

        // Determine status
        const statusTag = dayjs(interviewSchedule).isValid() &&
          dayjs().isAfter(dayjs(interviewSchedule)) ? (
          <Tag color="red">Expired</Tag>
        ) : (
          <Tag color="blue">Scheduled</Tag>
        );

        // Format date and time
        const date = dayjs(interviewSchedule)
          .tz(timeZone)
          .format("ddd DD MMM YYYY");
        const time = `${dayjs(interviewSchedule)
          .tz(timeZone)
          .format("h:mm A")} - ${dayjs(interviewSchedule)
          .tz(timeZone)
          .add(1, "hour")
          .format("h:mm A")}`;

        return (
          <div className="column-info-container">
            {statusTag}
            <Typography.Text type="secondary" className="text-secondary-small">
              {date}
            </Typography.Text>
            <Typography.Text type="secondary" className="text-secondary-small">
              {time}
            </Typography.Text>
          </div>
        );
      },
    });
  }

  columns.push({
    title: "Application Status",
    dataIndex: "status",
    key: "applicationStatus",
    minWidth: 150,
    fixed: "right" as const,
    className: isColumnFiltered("applicationStatus") ? "filtered-column" : "",
    render: (data) => {
      return (
        <Tag
          color={
            data === "hired"
              ? "green"
              : data === "rejected"
                ? "red"
                : data === "interview"
                  ? "blue"
                  : data === "shortlisted"
                    ? "orange"
                    : "default"
          }
          className="application-status-tag"
        >
          {data || "Applied"}
        </Tag>
      );
    },
  });

  columns.push({
    title: "Action Items",
    key: "action",
    minWidth: 100,
    fixed: "right" as const,
    render: (data: JobApplication) => {
      // Build menu items dynamically based on application state
      const menuItems: MenuProps["items"] = [
        {
          key: "view",
          label: "View",
          icon: <EyeOutlined style={{ color: themeColor }} />,
          onClick: (info) => {
            info.domEvent.stopPropagation();
            handleViewApplication(data._id);
          },
        },
      ];

      // Show "Send Resume Upload Reminder" only if applicable
      const canSendReminder = isEligibleForResumeReminder(data, jobPost);

      if (canSendReminder) {
        menuItems.push({
          key: "sendReminder",
          label: "Send Resume Upload Reminder",
          icon: <MailOutlined style={{ color: "#52c41a" }} />,
          onClick: (info) => {
            info.domEvent.stopPropagation();
            handleSendEmail(data);
          },
        });
      }

      // Show "Start Interview" only if applicable
      if (jobPost.onlineInterview && data?.interviewSchedule) {
        menuItems.push({
          type: "divider",
        });
        menuItems.push({
          key: "startInterview",
          label: "Start Interview",
          icon: <PlayCircleOutlined style={{ color: themeColor }} />,
          onClick: (info) => {
            info.domEvent.stopPropagation();
            handleStartInterview(data?._id);
          },
        });
      }

      // If only one action item, show direct button instead of dropdown
      if (menuItems.length === 1) {
        return (
          <Button
            type="text"
            icon={<EyeOutlined style={{ color: themeColor }} />}
            onClick={(e) => {
              e.stopPropagation();
              handleViewApplication(data._id);
            }}
            className="action-menu-button"
            title="View Application"
          />
        );
      }

      // Multiple actions: show dropdown menu
      return (
        <Dropdown
          menu={{ items: menuItems }}
          trigger={["hover"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined style={{ fontSize: 18 }} />}
            onClick={(e) => e.stopPropagation()}
            className="action-menu-button"
          />
        </Dropdown>
      );
    },
  });

  useEffect(() => {
    const updateTableHeight = () => {
      if (tableRef.current) {
        const computedStyle = getComputedStyle(tableRef.current);
        const heightValue = computedStyle.getPropertyValue(
          "--application-table-scroll-height",
        );
        if (heightValue) {
          const heightMatch = heightValue.match(
            /calc\([^)]*100\s*-\s*(\d+)px\)/,
          );
          if (heightMatch) {
            const vh = window.innerHeight;
            const offset = parseInt(heightMatch[1]);
            const calculatedHeight = vh - offset;
            setTableScrollHeight(Math.max(calculatedHeight, 200));
          }
        }
      }
    };

    updateTableHeight();

    window.addEventListener("resize", updateTableHeight);

    return () => {
      window.removeEventListener("resize", updateTableHeight);
    };
  }, []);

  // Add onCell to every column so action-required row background applies to all cells (including fixed columns)
  const ACTION_REQUIRED_ROW_BG = "#fff2f0";
  const columnsWithCellStyle: TableColumnsType<JobApplication> = columns.map(
    (col) => ({
      ...col,
      onCell: (record: JobApplication) =>
        isActionRequiredRow(record)
          ? { style: { backgroundColor: ACTION_REQUIRED_ROW_BG } }
          : {},
    }),
  );

  // Calculate total min-width for horizontal scroll
  const totalMinWidth = columnsWithCellStyle.reduce((total, column) => {
    return total + (column.minWidth || 100);
  }, 0);

  return (
    <section className="job-application-table-listing-section">
      <div className="job-application-listing-table-container" ref={tableRef}>
        <Table
          columns={columnsWithCellStyle}
          dataSource={jobApplications}
          scroll={{
            x: totalMinWidth,
            // y: tableScrollHeight,
          }}
          size="small"
          rowKey="_id"
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.totalRecords,
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} job applications`,
            onChange: (page, pageSize) => onPaginationChange(page, pageSize),
            onShowSizeChange: (current, size) =>
              onPaginationChange(current, size),
            showSizeChanger: false,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          rowHoverable
          rowClassName={(record) =>
            isActionRequiredRow(record)
              ? "cursor-pointer row-action-required"
              : "cursor-pointer"
          }
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys, selectedRows) => {
              if (onSelectChange) {
                onSelectChange(selectedKeys as string[], selectedRows);
              }
            },
            getCheckboxProps: (record) => ({
              name: record._id,
            }),
          }}
          onChange={(pagination, filters, sorter) => {
            if (onSortChange && sorter && !Array.isArray(sorter)) {
              const sortKey = sorter.field as string;
              const sortOrder = sorter.order as "ascend" | "descend" | null;

              // Map table sort keys to filter sort keys
              const sortKeyMap: Record<string, string> = {
                createdAt:
                  sortOrder === "ascend" ? "oldest_first" : "latest_first",
                appliedDate:
                  sortOrder === "ascend" ? "oldest_first" : "latest_first",
                candidate:
                  sortOrder === "ascend" ? "oldest_first" : "latest_first", // Name sorting uses same as date for now
                profileMatchStatus:
                  sortOrder === "ascend"
                    ? "profile_score_lowest"
                    : "profile_score_highest",
              };

              const mappedSortKey = sortKeyMap[sortKey];
              if (mappedSortKey) {
                onSortChange(mappedSortKey, sortOrder);
              }
            } else if (
              onSortChange &&
              Array.isArray(sorter) &&
              sorter.length > 0
            ) {
              const firstSorter = sorter[0];
              const sortKey = firstSorter.field as string;
              const sortOrder = firstSorter.order as
                | "ascend"
                | "descend"
                | null;

              const sortKeyMap: Record<string, string> = {
                createdAt:
                  sortOrder === "ascend" ? "oldest_first" : "latest_first",
                appliedDate:
                  sortOrder === "ascend" ? "oldest_first" : "latest_first",
                candidate:
                  sortOrder === "ascend" ? "oldest_first" : "latest_first",
                profileMatchStatus:
                  sortOrder === "ascend"
                    ? "profile_score_lowest"
                    : "profile_score_highest",
              };

              const mappedSortKey = sortKeyMap[sortKey];
              if (mappedSortKey) {
                onSortChange(mappedSortKey, sortOrder);
              }
            } else if (onSortChange) {
              // Clear sort
              onSortChange("", null);
            }
          }}
          onRow={(record) => ({
            "data-action-required": isActionRequiredRow(record)
              ? "true"
              : undefined,
            style: isActionRequiredRow(record)
              ? { backgroundColor: "var(--row-action-required-bg, #fff2f0)" }
              : undefined,
            onClick: (e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest(".ant-checkbox") ||
                target.closest(".ant-checkbox-wrapper") ||
                target.closest("button") ||
                target.closest(".ant-btn")
              ) {
                return;
              }

              // If any rows are selected, toggle selection on row click (bulk mode)
              // Otherwise, navigate to application details
              if (selectedRowKeys && selectedRowKeys.length > 0) {
                e.stopPropagation();
                // Toggle selection for this row
                const isSelected = selectedRowKeys.includes(record._id);
                const newSelectedKeys = isSelected
                  ? selectedRowKeys.filter((key) => key !== record._id)
                  : [...selectedRowKeys, record._id];

                // Only include rows from current page that are in newSelectedKeys
                // (rows from other pages will be handled by parent component)
                const newSelectedRows = jobApplications.filter((app) =>
                  newSelectedKeys.includes(app._id)
                );

                onSelectChange?.(newSelectedKeys, newSelectedRows);
              } else {
                // No selections yet, navigate to application details
                handleActionClick(e, () => {
                  handleTableRowClick(e, record);
                });
              }
            },
          })}
        />
      </div>
    </section>
  );
};

export default JobApplicationTable;
